/**
 * Interface language (English / Arabic) with right-to-left layout for Arabic.
 *
 * Scope: application chrome (navigation, headings, buttons, labels). Clinical content from
 * the engine (recommendation titles, rationale, guideline citations) stays in English, the
 * working language of UAE clinical documentation; patient-facing family plans are generated
 * in Arabic or the family's language.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "ar";

const EN = {
  patients: "Patients",
  manual: "Manual entry",
  integrations: "Integrations",
  audit: "Audit",
  signIn: "Sign in",
  signOut: "Sign out",
  signingIn: "Signing in…",
  username: "Username",
  password: "Password",
  loginSub: "Pulse PedObesity UAE · clinician decision support",
  loginEhr: "Launching from the EHR signs you in automatically. Sessions end after inactivity.",
  syntheticNotice: "Demo environment with synthetic patients only. Do not enter real patient information. Not for clinical use.",
  demoAccount: "Demo account",
  demoClinician: "Clinician",
  showPassword: "Show",
  hidePassword: "Hide",
  menu: "Menu",
  demoBanner: "Demo mode: synthetic patients only - do not enter real patient information. Decision support - every recommendation needs clinician review.",
  todaysPatients: "Today's patients",
  sortedBy: "Sorted by highest-severity recommendation",
  colPatient: "Patient",
  colAgeSex: "Age / sex",
  colWeight: "Weight status",
  colZ: "BMI z-score",
  colRecs: "Recommendations",
  colSource: "Source",
  urgent: "urgent",
  important: "important",
  routine: "routine",
  info: "info",
  bmi: "BMI (kg/m²)",
  bmiZ: "BMI z-score",
  percentile: "Percentile",
  bp: "Blood pressure",
  backToPatients: "← Patients",
  recsHeading: "Guideline recommendations",
  reviewEach: "{n} total · review each, then sign",
  urgentIndividual: "Urgent items need an individual decision",
  acceptAll: "Accept all non-urgent",
  accept: "Accept",
  notNow: "Not now",
  details: "Details",
  less: "Less",
  reasonRequired: "Reason (required)",
  signingAs: "Signing as",
  decided: "{n} decided",
  needReason: "add a reason for each declined item",
  confirm: "Confirm decisions",
  saving: "Saving…",
  recorded: "Recorded. Change a decision to record again.",
  aiAssistant: "AI assistant",
  tabBrief: "Pre-visit brief",
  tabFamily: "Family plan",
  tabMi: "MI coaching",
  tabAsk: "Ask the guideline",
  tabContext: "What AI sees",
  clinicalData: "Clinical data",
  missingData: "Missing data",
  footer: "Decision support only: this tool does not diagnose, order or prescribe.",
  clinicalEnglish: "Clinical content is shown in English.",
  language: "العربية",
  loading: "Loading…",
  dob: "DOB",
  male: "male",
  female: "female",
  planLanguage: "Language",
  familyContext: "Family context (optional, no identifiers)",
  draftPlan: "Draft family plan",
  drafting: "Drafting…",
  regenerate: "Regenerate",
  ask: "Ask",
  demoResetTitle: "Reset demo",
  demoResetHelp: "Removes manually entered and imported patients and all decisions, clears sign-in lockouts and restores the demo patients. The access log is kept.",
  demoResetButton: "Reset demo data",
  demoResetConfirm: "Reset the demo for everyone using it? Entered patients and decisions will be removed.",
  demoResetting: "Resetting…",
  demoResetDone: "Demo reset: {patients} entered patients and {decisions} decisions removed.",
} as const;

type Key = keyof typeof EN;

const AR: Record<Key, string> = {
  patients: "المرضى",
  manual: "إدخال يدوي",
  integrations: "التكامل",
  audit: "التدقيق",
  signIn: "تسجيل الدخول",
  signOut: "تسجيل الخروج",
  signingIn: "جارٍ تسجيل الدخول…",
  username: "اسم المستخدم",
  password: "كلمة المرور",
  loginSub: "Pulse PedObesity الإمارات · دعم القرار السريري للأطباء",
  loginEhr: "يتم تسجيل دخولك تلقائياً عند التشغيل من السجل الصحي الإلكتروني. تنتهي الجلسة بعد فترة من عدم النشاط.",
  syntheticNotice: "بيئة عرض تجريبية بمرضى افتراضيين فقط. لا تُدخل بيانات مرضى حقيقية. ليست للاستخدام السريري.",
  demoAccount: "الحساب التجريبي",
  demoClinician: "طبيب",
  showPassword: "إظهار",
  hidePassword: "إخفاء",
  menu: "القائمة",
  demoBanner: "وضع العرض التجريبي: مرضى افتراضيون فقط - لا تُدخل بيانات مرضى حقيقية. أداة لدعم القرار - كل توصية تتطلب مراجعة الطبيب.",
  todaysPatients: "مرضى اليوم",
  sortedBy: "مرتبة حسب أعلى درجة خطورة للتوصيات",
  colPatient: "المريض",
  colAgeSex: "العمر / الجنس",
  colWeight: "حالة الوزن",
  colZ: "درجة z لمؤشر كتلة الجسم",
  colRecs: "التوصيات",
  colSource: "المصدر",
  urgent: "عاجل",
  important: "مهم",
  routine: "روتيني",
  info: "معلومة",
  bmi: "مؤشر كتلة الجسم (كغ/م²)",
  bmiZ: "درجة z لمؤشر كتلة الجسم",
  percentile: "المئين",
  bp: "ضغط الدم",
  backToPatients: "→ المرضى",
  recsHeading: "توصيات الدليل الإرشادي",
  reviewEach: "{n} توصية · راجِع كل توصية ثم وقّع",
  urgentIndividual: "التوصيات العاجلة تتطلب قراراً فردياً",
  acceptAll: "قبول جميع التوصيات غير العاجلة",
  accept: "قبول",
  notNow: "ليس الآن",
  details: "التفاصيل",
  less: "إخفاء",
  reasonRequired: "السبب (مطلوب)",
  signingAs: "التوقيع باسم",
  decided: "{n} قرار",
  needReason: "أضِف سبباً لكل توصية مرفوضة",
  confirm: "تأكيد القرارات",
  saving: "جارٍ الحفظ…",
  recorded: "تم التسجيل. غيّر أي قرار لإعادة التسجيل.",
  aiAssistant: "المساعد الذكي",
  tabBrief: "ملخص ما قبل الزيارة",
  tabFamily: "خطة العائلة",
  tabMi: "المقابلة التحفيزية",
  tabAsk: "اسأل الدليل",
  tabContext: "ما يراه الذكاء الاصطناعي",
  clinicalData: "البيانات السريرية",
  missingData: "بيانات ناقصة",
  footer: "أداة لدعم القرار فقط: لا تقوم بالتشخيص أو طلب الفحوصات أو وصف الأدوية.",
  clinicalEnglish: "يُعرض المحتوى السريري باللغة الإنجليزية.",
  language: "English",
  loading: "جارٍ التحميل…",
  dob: "تاريخ الميلاد",
  male: "ذكر",
  female: "أنثى",
  planLanguage: "اللغة",
  familyContext: "معلومات عن العائلة (اختياري، بدون بيانات تعريفية)",
  draftPlan: "صياغة خطة العائلة",
  drafting: "جارٍ الصياغة…",
  regenerate: "إعادة الإنشاء",
  ask: "اسأل",
  demoResetTitle: "إعادة ضبط العرض التجريبي",
  demoResetHelp: "يحذف المرضى المُدخلين يدوياً أو المستوردين وجميع القرارات، ويرفع حظر تسجيل الدخول، ويعيد المرضى التجريبيين. يُحتفظ بسجل الوصول.",
  demoResetButton: "إعادة ضبط البيانات التجريبية",
  demoResetConfirm: "هل تريد إعادة ضبط العرض التجريبي لجميع المستخدمين؟ سيُحذف المرضى المُدخلون والقرارات.",
  demoResetting: "جارٍ إعادة الضبط…",
  demoResetDone: "تمت إعادة الضبط: حُذف {patients} من المرضى المُدخلين و{decisions} من القرارات.",
};

const DICTS: Record<Lang, Record<Key, string>> = { en: EN, ar: AR };

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (k: Key, vars?: Record<string, string | number>) => string };
const I18n = createContext<Ctx | null>(null);

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem("pedob_lang");
    if (saved === "ar" || saved === "en") return saved;
  } catch {
    /* storage blocked */
  }
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const value = useMemo<Ctx>(() => ({
    lang,
    setLang: (l) => {
      setLangState(l);
      try {
        localStorage.setItem("pedob_lang", l);
      } catch {
        /* storage blocked: keep for this page only */
      }
    },
    t: (k, vars) => {
      let s = DICTS[lang][k] ?? EN[k];
      for (const [name, v] of Object.entries(vars ?? {})) s = s.replace(`{${name}}`, String(v));
      return s;
    },
  }), [lang]);
  return <I18n.Provider value={value}>{children}</I18n.Provider>;
}

export function useI18n(): Ctx {
  const c = useContext(I18n);
  if (!c) throw new Error("useI18n outside I18nProvider");
  return c;
}

/** DD/MM/YYYY, the UAE convention, from an ISO date. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
